/**
 * EventVisibilityToggle Component
 * 
 * Shared component for event visibility control in both Create and Edit pages.
 * Handles public/members-only toggle with club privacy constraint enforcement.
 * 
 * Issue #427: Event Visibility Toggle
 */

import { Eye } from 'lucide-react';

interface EventVisibilityToggleProps {
  value: 'public' | 'members_only';
  onChange: (visibility: 'public' | 'members_only') => void;
  clubVisibility?: 'public' | 'private' | 'invite_only';
}

export default function EventVisibilityToggle({ 
  value, 
  onChange, 
  clubVisibility 
}: EventVisibilityToggleProps) {
  const isLockedClub = clubVisibility === 'private' || clubVisibility === 'invite_only';
  
  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Eye className="w-4 h-4 text-text-muted" />
        <h3 className="font-semibold text-text-primary">Event Visibility</h3>
      </div>
      
      <div className="space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="visibility"
            value="public"
            checked={value === 'public'}
            onChange={() => onChange('public')}
            disabled={isLockedClub}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="font-medium text-text-primary">Public</div>
            <div className="text-sm text-text-secondary">
              Anyone with the link can see this event.
            </div>
          </div>
        </label>
        
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="visibility"
            value="members_only"
            checked={value === 'members_only'}
            onChange={() => onChange('members_only')}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="font-medium text-text-primary">Members-only</div>
            <div className="text-sm text-text-secondary">
              Only members of the host club can see this event.
            </div>
          </div>
        </label>
        
        {isLockedClub && (
          <div className="text-sm text-text-secondary bg-oro-surface-alt border border-oro-border rounded-lg p-3">
            ℹ️ This club is {clubVisibility}, so events must be members-only.
          </div>
        )}
      </div>
    </div>
  );
}
